import styles from './TooltipV2.module.scss';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { cancelRemoveTooltipContent, removeTooltipContent } from '../../../store/tooltip/tooltip_actions';

function TooltipV2() {
    const dispatch = useAppDispatch();
    const { position, content, showTooltip, placement } = useAppSelector((state) => state.tooltipReducer);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState<any>({ top: 0, left: 0 });

    useEffect(() => {
        if (position && showTooltip && tooltipRef.current) {
            const tooltipElement = tooltipRef.current;
            if (tooltipElement) {
                const tooltipRect = tooltipElement.getBoundingClientRect();
                const newCoords = calculatePosition(position, tooltipRect, placement);
                newCoords && setCoords(newCoords);
            }
        } else {
            setCoords(null);
        }
    }, [position, showTooltip, placement, tooltipRef.current]);

    const calculatePosition = (position, tooltipRect, placement) => {
        const { top, left, width, height } = position;
        const { width: tooltipWidth, height: tooltipHeight } = tooltipRect;
        const offset = 10; // 10px offset

        let base;
        switch (placement) {
            case 'top':
                base = { top: top - tooltipHeight - offset, left: left + width / 2 - tooltipWidth / 2 };
                break;
            case 'bottom':
                base = { top: top + height + offset, left: left + width / 2 - tooltipWidth / 2 };
                break;
            case 'left':
                base = { top: top + height / 2 - tooltipHeight / 2, left: left - tooltipWidth - offset };
                break;
            case 'right':
                base = { top: top + height / 2 - tooltipHeight / 2, left: left + width + offset };
                break;
            default:
                return null;
        }

        // Clamp to the viewport with an edge padding so the tooltip never renders
        // off-screen (e.g. a pill near the right/left edge on mobile). The tooltip
        // is position: fixed, so bounds are the viewport.
        const EDGE_PADDING = 12;
        const maxLeft = window.innerWidth - tooltipWidth - EDGE_PADDING;
        const maxTop = window.innerHeight - tooltipHeight - EDGE_PADDING;
        return {
            left: Math.max(EDGE_PADDING, Math.min(base.left, Math.max(EDGE_PADDING, maxLeft))),
            top: Math.max(EDGE_PADDING, Math.min(base.top, Math.max(EDGE_PADDING, maxTop))),
        };
    };

    const handleMouseEnter = () => {
        cancelRemoveTooltipContent();
    };

    const handleMouseLeave = () => {
        removeTooltipContent(dispatch);
    };

    return position && showTooltip ? (
        <div
            ref={tooltipRef}
            className={clsx(styles.TooltipV2, { [styles.TooltipV2__Show]: showTooltip && !!coords })}
            style={{
                top: coords?.top,
                left: coords?.left,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {content}
        </div>
    ) : (
        <></>
    );
}

export default TooltipV2;
